/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import clsx from "clsx";
import {
    AtSymbolIcon,
    BuildingOffice2Icon,
    ChatBubbleBottomCenterTextIcon,
    EnvelopeOpenIcon,
    MapIcon,
    MapPinIcon,
    PhoneIcon,
    RectangleStackIcon,
    UserIcon,
    UsersIcon,
} from "@heroicons/react/24/outline";
import {useTranslation} from "react-i18next";
import {getAutoComplete} from "utils/autoCompleteUtils";

interface LabelProps {
    name: string;
    label: string;
}

export const Label = (props: LabelProps) => {
    return (
        <label className="block text-sm font-medium text-gray-900" htmlFor={"input_" + props.name}>
            <span>{props.label}</span>
        </label>
    );
};

interface InputProps {
    register: any;
    name: string;
    label: string | "undefined";
    type?: string;
    id?: string;
    // value?: string | number | null;
    className?: string;
    placeholder?: string | number | null | any;
    required?: boolean | null;
    errors?: any;
    onChange?: any;
    onEnter?: any;
    labelLess?: boolean;
    disabled?: boolean;
}

export const Input = (props: InputProps) => {
    const {t} = useTranslation();
    let type = "text";
    if (props.type !== undefined) {
        type = props.type;
    }
    const className = clsx(
        props.className,
        props.disabled && "grayscale bg-gray-400",
        type === "text" || type === "email" || type === "number" || type === "tel" ? "py-3 px-3 h-[50px]" : "p-1",
        " mt-0 border rounded-md shadow-xs focus:outline-none active:ring-cta-800 active:ring-2 active:ring-offset-2 focus:border-cta-800 checked:bg-cta-800 sm:text-sm",
        props.errors?.[props.name] ? "ring-2 ring-red-500 border-red-500" : "border-gray-800"
    );
    const iconClass = "h-5 w-5 text-black text-opacity-30";
    const registerOptions =
        props.name === "email"
            ? {
                  required: props.required,
                  pattern: {
                      value: /^([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x22([^\x0d\x22\x5c\x80-\xff]|\x5c[\x00-\x7f])*\x22)(\x2e([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x22([^\x0d\x22\x5c\x80-\xff]|\x5c[\x00-\x7f])*\x22))*\x40([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x5b([^\x0d\x5b-\x5d\x80-\xff]|\x5c[\x00-\x7f])*\x5d)(\x2e([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x5b([^\x0d\x5b-\x5d\x80-\xff]|\x5c[\x00-\x7f])*\x5d))*$/,
                      message: "Email is incorrectly formed.",
                  },
              }
            : {
                  required: props.required,
              };

    if (props.labelLess === true) {
        return (
            <input
                {...props.register(props.name, registerOptions)}
                className={className}
                disabled={props.disabled}
                id={"input_" + props.name}
                name={props.name}
                type={props.type || "text"}
                autoComplete={getAutoComplete(props.name)}
                onChange={props.onChange}
                onKeyPress={props.onEnter}
            />
        );
    }

    return (
        <div className={"BookingInput relative"}>
            <div className={"relative flex flex-row items-center space-x-2"}>
                <Label label={props.label} name={props.name} />
                {props.required && <span className={"font-bold text-red-500"}>* </span>}
            </div>
            <div className="mt-0 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 h-full left-0 pl-3 flex items-center pointer-events-none z-20">
                    {props.name === "company_name" && <BuildingOffice2Icon aria-hidden="true" className={iconClass} />}
                    {props.name === "cin" && <RectangleStackIcon aria-hidden="true" className={iconClass} />}
                    {props.name === "street_address" && <MapPinIcon aria-hidden="true" className={iconClass} />}
                    {props.name === "city" && <MapIcon aria-hidden="true" className={iconClass} />}
                    {props.name === "postal_code" && <EnvelopeOpenIcon aria-hidden="true" className={iconClass} />}
                    {props.name === "first_name" && <UserIcon aria-hidden="true" className={iconClass} />}
                    {props.name === "name" && <UserIcon aria-hidden="true" className={iconClass} />}
                    {props.name === "last_name" && <UsersIcon aria-hidden="true" className={iconClass} />}
                    {props.name === "email" && <AtSymbolIcon aria-hidden="true" className={iconClass} />}
                    {props.name === "phone" && <PhoneIcon aria-hidden="true" className={iconClass} />}
                    {props.name === "message" && <ChatBubbleBottomCenterTextIcon aria-hidden="true" className={iconClass} />}
                </div>
                {props.name === "message" ? (
                    <textarea
                        {...props.register(props.name, {required: props.required})}
                        className={className}
                        disabled={props.disabled}
                        id={"input_" + props.name}
                        name={props.name}
                        onChange={props.onChange}
                    />
                ) : (
                    <input
                        {...props.register(props.name, registerOptions)}
                        className={className}
                        disabled={props.disabled}
                        id={"input_" + props.name}
                        name={props.name}
                        type={props.type || "text"}
                        autoComplete={getAutoComplete(props.name)}
                        onChange={props.onChange}
                        onKeyPress={props.onEnter}
                    />
                )}
            </div>
        </div>
    );
};
